import React, { useEffect, useState } from "react";
import { MdExpandLess, MdExpandMore  } from "react-icons/md";
import {
  calculateProfitMargin,
  calculateBreakevenSalePrice,
  calculateEstimatedAmazonPayout,
  calculateROI,
  calculateMaximumCost,
  miscFeeCalc,
} from "../../utils/profitCalculator";
import TooltipSimple from "../Common/TooltipSimple";

import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { FaArrowTrendUp } from "react-icons/fa6";
import { FaArrowTrendDown } from "react-icons/fa6";
import ToggleWithText from "../Common/ToggleWithText";
import { getCurrency } from "../../utils/helper";

const ProfitCalculator = ({ data, asin, settingsData }) => {
  const initialPrices = data?.prices;
  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(true);
  const [quantity, setQuantity] = useState(1);

  const prices = useSelector((state) => state.globalStates.prices);
  const selectedFulfillment = useSelector(
    (state) => state.globalStates.selectedFulfillment
  );
  const fullfillmentFees = useSelector(
    (state) => state.globalStates.fullfillmentFees
  );
  const totalFees = useSelector((state) => state.globalStates.total_fees);
  const settings = useSelector((state) => state.globalStates.settings);

  const [storageMonths, setStorageMonths] = useState(1);
  const [storageFeesPerMonth, setStorageFeesPerMonth] = useState(0);
  const [profit, setProfit] = useState(0);
  const [profitMargin, setProfitMargin] = useState(0);
  const [breakevenSalePrice, setBreakevenSalePrice] = useState(0);
  const [estimatedAmazonPayout, setEstimatedAmazonPayout] = useState(0);
  const [roi, setROI] = useState(0);
  const [maximumCost, setMaximumCost] = useState(0);
  const [referralFeePercentage, setReferralFeePercentage] = useState(0);
  const [referalFee, setReferalFee] = useState(0);
  const [miscFee, setMiscFee] = useState(0);
  const [inboundPlacementFee, setInboundPlacementFee] = useState(0);

  const calculateTotalFees = () => {
    let totalFees = 0;
    const safeNumber = (value) => {
      const num = Number(value);
      return isNaN(num) ? 0 : num;
    };

    let storageFees = safeNumber(
      (storageFeesPerMonth * storageMonths).toFixed(2)
    );

    const referalFee = Number(
      (
        (safeNumber(prices.sale_price) * safeNumber(referralFeePercentage)) /
        100
      ).toFixed(2)
    );
    totalFees += safeNumber(referalFee);
    // totalFees += selectedFulfillment === 'FBA' ? safeNumber(data?.total_fees?.fba_breakdown?.FBAFees) : safeNumber(data?.total_fees?.fbm_breakdown?.FBMFees);
    totalFees += selectedFulfillment === "FBA" ? storageFees : 0;
    totalFees +=
      selectedFulfillment === "FBA"
        ? safeNumber(data?.total_fees?.fba_breakdown?.prep_fee)
        : 0;
    totalFees += safeNumber(data?.total_fees?.fba_breakdown?.closingFees);
    totalFees +=
      selectedFulfillment === "FBA"
        ? safeNumber(data?.total_fees?.fba_breakdown?.inbound_shipping)
        : 0;
    totalFees += Number(data?.total_fees?.fba_breakdown?.misc_fee);
    totalFees += Number(miscFee);
    totalFees += Number(selectedFulfillment === "FBA" ? safeNumber(data?.total_fees?.fba_breakdown?.inbound_placement_fee) : 0);
    totalFees +=
      selectedFulfillment === "FBA"
        ? safeNumber(fullfillmentFees.FBA)
        : safeNumber(fullfillmentFees.FBM);
    totalFees = Number(totalFees.toFixed(2));

    dispatch({
      type: "SET_TOTAL_FEES",
      payload: totalFees,
    });
    
    return totalFees;
  };
  
  useEffect(() => {
    if (settings?.defaultValues?.local_fullfillment) {
      dispatch({
        type: "SET_SELECTED_FULFILLMENT",
        payload: settings?.defaultValues?.local_fullfillment,
      });
    }
  }, [settings]);

  useEffect(() => {
    if (data) {
      const cost_prices_object = JSON.parse(
        localStorage.getItem("cost_prices")
      );
      if (cost_prices_object && cost_prices_object.hasOwnProperty(asin) && asin !== "-") {
        dispatch({
          type: "SET_PRICES",
          payload: {
            sale_price: initialPrices?.sale_price || 0,
            cost_price:
              cost_prices_object[asin] || data.prices?.cost_price || "",
          },
        });
      } else {
        dispatch({
          type: "SET_PRICES",
          payload: {
            cost_price:
              initialPrices?.cost_price && Number(initialPrices?.cost_price) >= 0
                ? initialPrices?.cost_price
                : 0,
            sale_price: initialPrices?.sale_price || 0,
          },
        });
      }

      let storageDuration = data?.storageDuration;
      let storageFee = data?.total_fees?.fba_breakdown?.storageFees;
      let storageFeePerMonth =
        data?.total_fees?.fba_breakdown?.storageFeesPerMonth;

      storageDuration = Number(storageDuration);
      storageFee = Number(storageFee);
      storageFeePerMonth = Number(storageFeePerMonth);

      // if (storageDuration && storageFee) {
      //   let storageFeePM = storageFee / storageDuration;
      //   storageFeePM = Number(storageFeePM.toFixed(2));
      //   setStorageFeesPerMonth(storageFeePM);
      // }
      setStorageFeesPerMonth(storageFeePerMonth);
      setStorageMonths(storageDuration);

      const fbaFees = data?.total_fees?.fba_breakdown?.FBAFees;
      const fbmFees = data?.total_fees?.fbm_breakdown?.FBMFees;

      dispatch({
        type: "SET_FULFILLMENT_FEES",
        payload: {
          FBA: fbaFees,
          FBM: fbmFees,
        },
      });

      const referalFeePercent =
        data?.total_fees?.fba_breakdown?.referralFeePercent;
      const referalFee = (
        (parseFloat(prices.sale_price) * parseFloat(referalFeePercent)) /
        100
      ).toFixed(2);

      const salePrice = parseFloat(prices.sale_price);
      const costPrice = parseFloat(prices.cost_price);

      const amazonFees = totalFees;

      let profitData = calculateProfitMargin(salePrice, costPrice, amazonFees);
      setProfit(profitData[0]);
      setProfitMargin(profitData[1]);
      setBreakevenSalePrice(
        calculateBreakevenSalePrice(
          costPrice,
          salePrice,
          amazonFees,
          data?.total_fees?.fba_breakdown?.referralFeePercent
        )
      );
      setEstimatedAmazonPayout(
        calculateEstimatedAmazonPayout(salePrice, amazonFees)
      );
      setROI(calculateROI(salePrice, costPrice, amazonFees).toFixed(2));
      setMaximumCost(data?.max_cost);
      setReferralFeePercentage(referalFeePercent);
      setReferalFee(referalFee);
      setMiscFee(
        miscFeeCalc(
          costPrice,
          data?.total_fees?.fba_breakdown?.misc_fee_percentage
        )
      );
      setInboundPlacementFee(data?.total_fees?.fba_breakdown?.inbound_placement_fee)
    }
  }, [data]);

  useEffect(() => {
    const fee = calculateTotalFees();

    dispatch({
      type: "SET_TOTAL_FEES",
      payload: fee,
    });
  }, [
    selectedFulfillment,
    storageMonths,
    fullfillmentFees,
    miscFee,
    referalFee,
  ]);

  useEffect(() => {
    let storageDuration = data?.storageDuration;
    let storageFee = data?.total_fees?.fba_breakdown?.storageFees;

    storageDuration = Number(storageDuration);
    storageFee = Number(storageFee);

    const fbaFees = data?.total_fees?.fba_breakdown?.FBAFees;
    const fbmFees = data?.total_fees?.fbm_breakdown?.FBMFees;

    // Call functions to update calculated values
    const amazonFees = totalFees;
    const salePrice = parseFloat(prices.sale_price);
    const costPrice = parseFloat(prices.cost_price);

    let profitData = calculateProfitMargin(salePrice, costPrice, amazonFees);
    setProfit(profitData[0]);
    setProfitMargin(profitData[1]);
    setBreakevenSalePrice(
      calculateBreakevenSalePrice(
        costPrice,
        salePrice,
        amazonFees,
        data?.total_fees?.fba_breakdown?.referralFeePercent
      )
    );
    setEstimatedAmazonPayout(
      calculateEstimatedAmazonPayout(salePrice, amazonFees)
    );
    setROI(calculateROI(salePrice, costPrice, amazonFees).toFixed(2));
    // maxcost only changes if sale price changes or fullfilment changes
    if (
      prices.sale_price !== initialPrices?.sale_price ||
      (settings?.defaultValues?.local_fullfillment &&
        settings?.defaultValues?.local_fullfillment !== selectedFulfillment)
    ) {
      let maxcost = calculateMaximumCost(
        salePrice,
        costPrice,
        amazonFees,
        Number(settings?.buyingCriteria?.minProfit) || 0,
        profitData[0],
        settings
      );
      setMaximumCost(maxcost);
      if (Number(prices.sale_price) < 10) {
        dispatch({
          type: "SET_FULFILLMENT_FEES",
          payload: {
            ...fullfillmentFees,
            FBA:
              Number(initialPrices?.sale_price) >= 10
                ? parseFloat(
                    Number(data?.total_fees?.fba_breakdown?.FBAFees) - 0.77
                  ).toFixed(2)
                : data?.total_fees?.fba_breakdown?.FBAFees,
          },
        });
      } else {
        dispatch({
          type: "SET_FULFILLMENT_FEES",
          payload: {
            ...fullfillmentFees,
            FBA:
              Number(initialPrices?.sale_price) < 10
                ? parseFloat(
                    Number(data?.total_fees?.fba_breakdown?.FBAFees) + 0.77
                  ).toFixed(2)
                : data?.total_fees?.fba_breakdown?.FBAFees,
          },
        });
      }
    } else {
      setMaximumCost(data?.max_cost);
    }
    setMiscFee(
      miscFeeCalc(
        costPrice,
        data?.total_fees?.fba_breakdown?.misc_fee_percentage
      )
    );
    setInboundPlacementFee(selectedFulfillment === "FBA" ? data?.total_fees?.fba_breakdown?.inbound_placement_fee : '0.00')
    const newReferralFee = (
      (parseFloat(prices.sale_price) *
        parseFloat(data?.total_fees?.fba_breakdown?.referralFeePercent)) /
      100
    ).toFixed(2);
    setReferalFee(newReferralFee);
  }, [prices.sale_price, prices.cost_price, totalFees, selectedFulfillment]);

  const dropdownOptions = ["FBA", "FBM"];
  const handleDropdownChange = (event) => {
    dispatch({
      type: "SET_SELECTED_FULFILLMENT",
      payload: event,
    });
  };

  const formatValue = (value) => {
    if (isNaN(value)) {
      return `${getCurrency(settingsData?.marketplace)}0`;
    }

    if (value < 0) {
      return `${getCurrency(settingsData?.marketplace)}${Math.abs(value)}`;
    }

    return `${getCurrency(settingsData?.marketplace)}${parseFloat(value).toFixed(2)}`;
  };

  const handleCostChange = (e, index) => {
    let { value } = e.target;
    value = value.replace(getCurrency(settingsData?.marketplace), "");
    value = e.target.value.replace(/[^0-9.]/g, "");
    if (value.length > 2) {
      const dollars = value.slice(0, -2);
      const cents = value.slice(-2);
      value = `${dollars}.${cents}`;
    }

    if (index === 0) {
      dispatch({
        type: "SET_PRICES",
        payload: {
          ...prices,
          cost_price: value && Number(value) >= 0 ? value : 0,
        },
      });
    } else {
      dispatch({
        type: "SET_PRICES",
        payload: {
          ...prices,
          sale_price: value,
        },
      });
    }
  };

  const handleFbmChange = (e) => {
    let { value } = e.target;
    value = value.replace(getCurrency(settingsData?.marketplace), "");
    value = e.target.value.replace(/[^0-9.]/g, "");
    if (value.length > 2) {
      const dollars = value.slice(0, -2);
      const cents = value.slice(-2);
      value = `${dollars}.${cents}`;
    }
    dispatch({
      type: "SET_FULFILLMENT_FEES",
      payload: {
        ...fullfillmentFees,
        FBM: value,
      },
    });
  };

  const properties2 = [
    {
      title: "Profit",
      value: profit,
      label: getCurrency(settingsData?.marketplace) + profit,
    },
    {
      title: "ROI",
      label: roi + "%",
      value: roi,
    },
    {
      title: "Maximum Cost",
      value: maximumCost,
      label: formatValue(maximumCost),
    },
  ];

  const properties3 = [
    {
      title: "Total Fees",
      value: formatValue(totalFees),
    },
    {
      title: "Referral Fee",
      value: formatValue(referalFee),
    },
    {
      title: "Fulfillment (" + selectedFulfillment + ")",
      value: formatValue(fullfillmentFees[selectedFulfillment]),
    },
    {
      title: "Storage Fees",
      value: formatValue((storageFeesPerMonth * storageMonths).toFixed(2)),
      strike: selectedFulfillment === "FBM" ? true : false,
    },
    {
      title: "Prep Fees",
      value: formatValue(data?.total_fees?.fba_breakdown?.prep_fee),
      strike: selectedFulfillment === "FBM" ? true : false,
    },
    {
      title: "Closing Fees",
      value: formatValue(data?.total_fees?.fba_breakdown?.closingFees),
    },
    {
      title: "Inbound Shipping",
      value: formatValue(data?.total_fees?.fba_breakdown?.inbound_shipping),
      strike: selectedFulfillment === "FBM" ? true : false,
      // tooltipText: 'Estimated Maximum Inbound Placement Service fee for Amazon Optimized Splits'
    },
    {
      title: "Misc Fees",
      value: getCurrency(settingsData?.marketplace) + data?.total_fees?.fba_breakdown?.misc_fee,
    },
    {
      title: "Misc Fees (% of cost)",
      value: getCurrency(settingsData?.marketplace) + miscFee,
    },
    {
      title: "Inbound Placement Fees (FBA)",
      value: getCurrency(settingsData?.marketplace) + inboundPlacementFee,
    }
  ];

  const properties4 = [
    {
      title: "Profit Margin",
      value: profitMargin,
      highlighted: true,
    },
    {
      title: "Breakeven Sale Price",
      value: formatValue(breakevenSalePrice),
      highlighted: true,
    },
    {
      title: "Estimated Amz. Payout",
      value: formatValue(estimatedAmazonPayout),
      highlighted: true,
    },
  ];

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div
      className="flex flex-col w-full"
    >
      <div className="">
        <div
          className="flex cursor-pointer items-center mb-2 justify-between"
          onClick={toggleAccordion}
        >
          <div
            className="mr-1 text-primary text-md font-bold"
          >
            Profit Calculator
          </div>
          <div className="text-primary">
            {isOpen ? <MdExpandMore className="w-6 h-6" /> : <MdExpandLess className="w-6 h-6" />}
          </div>
        </div>
        {isOpen && (
          <>
            {/* Cost and Sale price side-by-side */}
            <div className="flex gap-2 align-center mt-2">
                <div className="flex items-center bg-secondary-bg p-4 rounded-lg">
                  <div className="text-left text-white text-sm w-full">Cost Price</div>
                  <input
                    type="text"
                    value={`${getCurrency(settingsData?.marketplace)}${prices.cost_price}`}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, "");
                      e.target.value = `${getCurrency(settingsData?.marketplace)}${rawValue}`;
                      handleCostChange(e, 0);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === ".") {
                        e.preventDefault();
                      }
                    }}
                    className={`w-full text-center text-sm text-white border border-primary rounded-md bg-primary-bg`}
                    style={{ minHeight: '30px' }}
                  />
                </div>
                <div className="flex items-center bg-secondary-bg p-4 rounded-lg">
                  <div className="text-left text-white text-sm w-full">Sale Price</div>
                  <input
                    type="text"
                    value={`${getCurrency(settingsData?.marketplace)}${prices.sale_price}`}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, "");
                      e.target.value = `${getCurrency(settingsData?.marketplace)}${rawValue}`;
                      handleCostChange(e, 1);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === ".") {
                        e.preventDefault();
                      }
                    }}
                    className={`w-full text-center text-sm text-white rounded-md border border-primary bg-primary-bg`}
                    style={{ minHeight: '30px' }}
                  />
                </div>
            </div>

            {/* Fulfillment row remains as is */}
            <div
              className="flex items-center justify-between bg-secondary-bg p-4 rounded-lg align-center mt-2"
            >
              <div className="w-3/4 text-sm text-white text-left">
                Fullfillment
              </div>
              <div className="w-full flex items-center justify-end">
                <ToggleWithText
                  options={["FBA", "FBM"]}
                  value={selectedFulfillment}
                  onChange={handleDropdownChange}
                />
              </div>
            </div>
            {/* SHOW INPUT FOR ADDING FBM FEE */}
            {selectedFulfillment === "FBM" && (
              <div
                className="flex justify-between bg-secondary-bg p-4 rounded-lg items-center mt-2"
              >
                <div className="text-sm text-white text-left">
                  FBM Fee
                </div>
                  <input
                    type="text"
                    value={`${getCurrency(settingsData?.marketplace)}${fullfillmentFees.FBM}`}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, "");
                      e.target.value = `${getCurrency(settingsData?.marketplace)}${rawValue}`;
                      handleFbmChange(e);
                    }}
                    className={`text-center text-white rounded-md border border-primary bg-primary-bg w-[25%]`}
                  />
              </div>
            )}
            {selectedFulfillment === "FBA" && (
              <div
                className="flex justify-between bg-secondary-bg p-4 rounded-lg align-center mt-2"
                style={{ fontSize: "13px" }}
              >
                <div className="w-3/4 text-white text-sm" style={{ textAlign: "left" }}>
                  Storage Months
                </div>
                <div className="w-1/4 flex items-center">
                  <input
                    type="range"
                    min="0"
                    max="12"
                    value={storageMonths}
                    onChange={(e) => {
                      setStorageMonths(e.target.value);
                    }}
                    className="slider w-full"
                  />
                  <span className="ml-2 text-primary">{storageMonths}</span>
                </div>
              </div>
            )}

            <div className="bg-secondary-bg p-4 rounded-xl mt-2">
              {properties2.map((property, index) => (
                <div
                  key={index}
                  className={`flex justify-between text-sm text-white`}
                >
                  <div>{property.title}</div>
                  {property.title === "Maximum Cost" ? (
                    <div
                      className={`flex relative group items-center ${
                        Number(property?.value) < 0
                          ? "bg-[#E74B4B]/20 text-[#E74B4B] p-1 rounded-md mb-2 border border-[#E74B4B]"
                          : "bg-primary/20 text-primary p-1 rounded-md mb-2 font-semibold border border-primary"
                      }`}
                    >
                      {property.label}
                      {Number(property?.value) <= 0 && (
                        <TooltipSimple
                          width={"extra"}
                          text={`The maxcost of product is ${getCurrency(settingsData?.marketplace)}${property?.value} which is not profitable.`}
                          position={"left-[10%]"}
                        />
                      )}
                      {Number(property?.value) > 0 ? (
                        <FaArrowTrendUp className="ml-1" />
                      ) : null}
                    </div>
                  ) : (
                    <div
                      className={`flex  items-center ${
                        Number(property?.value) < 0
                          ? "bg-[#E74B4B]/20 text-[#E74B4B] p-1 rounded-md mb-2 border border-[#E74B4B]"
                          : "bg-primary/20 text-primary p-1 rounded-md mb-2 font-semibold border border-primary"
                      }`}
                    >
                      {property.label}
                      {Number(property?.value) < 0 ? (
                        <FaArrowTrendDown className="ml-1" />
                      ) : (
                        <FaArrowTrendUp className="ml-1" />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="bg-secondary-bg p-4 rounded-xl mt-2">
              {properties3.map((property, index) => (
                <div
                  key={index}
                  className="flex justify-between align-center mb-2"
                >
                  {property.highlighted ? (
                    <>
                      <p className="text-primary text-sm">
                        {property.title}
                        {property?.tooltipText && (
                          <TooltipSimple title={property.tooltipText} />
                        )}
                      </p>
                      <p className="text-primary text-sm">{property.value}</p>
                    </>
                  ) : (
                    <>
                      <p
                        className={
                        property.strike ? "line-through text-sm text-white flex" : "text-sm text-white flex"
                        }
                      >
                        {property.title}
                        {property?.tooltipText && (
                          <TooltipSimple title={property.tooltipText} />
                        )}
                      </p>
                      <p className="text-primary text-sm">{property.value}</p>
                    </>
                  )}
                </div>
              ))}
            </div>

            <div className="bg-secondary-bg p-4 rounded-xl mt-2">
              {properties4.map((property, index) => (
                <div
                  key={index}
                  className="flex justify-between align-center mb-1"
                >
                  {property.highlighted ? (
                    <>
                      <p className="text-primary text-sm">
                        {property.title}
                      </p>
                      <p className="text-primary text-sm">{property.value}</p>
                    </>
                  ) : (
                    <>
                      <p
                        className={property.strike ? "line-through text-sm text-white flex" : "text-sm text-white flex"}
                      >
                        {property.title}
                      </p>
                      <p className="text-primary text-sm">{property.value}</p>
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="bg-secondary-bg p-4 rounded-xl mt-2">
              <div
                className="flex justify-between items-center"
              >
                <p className="text-white text-sm">
                  Quantity
                </p>
                  <input
                    type="number"
                    value={quantity}
                    className={`text-center text-sm text-white border border-primary rounded-md bg-primary-bg max-w-[10%] no-spinner`}
                    onChange={(e) => setQuantity(e.target.value)}
                  />
              </div>
              {quantity && quantity > 1 && (
                <>
                  <div
                    className="flex justify-between"
                    style={{ fontSize: "13px" }}
                  >
                    <div className="text-primary font-bold mt-1">
                      Cost
                    </div>
                    <div className="text-primary">
                      {formatValue((prices.cost_price * quantity).toFixed(2))}
                    </div>
                  </div>
                  <div
                    className="flex justify-between"
                    style={{ fontSize: "13px" }}
                  >
                    <div className="text-primary font-bold mt-1">
                      Sale
                    </div>
                    <div className="text-primary">
                      {formatValue((prices.sale_price * quantity).toFixed(2))}
                    </div>
                  </div>
                  <div
                    className="flex justify-between"
                    style={{ fontSize: "13px" }}
                  >
                    <div className="text-primary font-bold mt-1">
                      Total Profit
                    </div>
                    <div className="text-primary">
                      {formatValue((profit * quantity).toFixed(2))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProfitCalculator;
